import client from './client'
import {
  CreateOrg,
  ListResponse,
  GetApplications,
  ApplicationInfo,
  UpdateOrganizationinfo,
  Organizationinfo,
  MemberInfo,
  GetOrgMembers,
  GetRoles,
  DeleteOrgMember,
  RoleInfo,
  UpdateUserRole,
  InviteMembers,
  DeleteRole,
  PermissionInfo,
  UpdateRolePermission,
  CreateRoles,
  APITokenInfo,
  CrateAPITokenInfo,
  AppListResponse,
  PutOrgReq,
} from '@/types'

export const createOrg = (data: CreateOrg) => {
  return client<Organizationinfo>({
    url: 'organizations',
    method: 'post',
    data,
  })
}

export const leaveOrg = (orgId: number) => {
  return client<unknown>({
    url: `/organizations/${orgId}/user/leave`,
    method: 'POST',
  })
}

export const dissolveOrg = (orgId: number) => {
  return client<unknown>({
    url: `/organizations/${orgId}/dissolve`,
    method: 'POST',
  })
}

export const getAllApplications = (params: GetApplications) => {
  return client<ListResponse<ApplicationInfo>>({
    url: '/apps',
    method: 'get',
  })
}

export const getOrgsApplications = (params: GetApplications) => {
  return client<ListResponse<ApplicationInfo>>({
    url: `organizations/${params.org_id}/apps`,
    method: 'get',
    params: { ...params.query },
  }).then((res) => {
    if (res?.data?.length) {
      res.data.forEach((item) => {
        const url_prefix_arr = item.url_prefix.split('.')
        url_prefix_arr[0] = `${url_prefix_arr[0]}-${params.org_id}`
        item.url_prefix = url_prefix_arr.join('.')
      })
    }
    return res
  })
}

export const getPurchasedApplications = (id: number) => {
  return client<AppListResponse<ApplicationInfo>>({
    url: `organizations/${id}/purchase/apps`,
    method: 'get',
  }).then((res) => {
    if (res?.apps?.length) {
      res.apps.forEach((item) => {
        const url_prefix_arr = item.url_prefix.split('.')
        url_prefix_arr[0] = `${url_prefix_arr[0]}-${id}`
        item.url_prefix = url_prefix_arr.join('.')
      })
    }
    return res
  })
}

export const getPurchasedNotifiedApplications = (id: number) => {
  return client<AppListResponse<ApplicationInfo>>({
    url: `organizations/${id}/purchase/apps-with-msg-func`,
    method: 'get',
  })
}

export const setDefaultOrg = (data: { ID: number }) => {
  return client<unknown>({
    url: 'organizations/default',
    method: 'put',
    data,
  })
}

export const getOrgInfo = (params: number) => {
  return client<Organizationinfo>({
    method: 'get',
    url: `organizations/${params}`,
  })
}

export const updateOrgInfo = (data: UpdateOrganizationinfo) => {
  return client<unknown>({
    method: 'put',
    url: `organizations/${data.id}`,
    data: { ...data.info },
  })
}

export const getMemebers = (params: GetOrgMembers) => {
  return client<ListResponse<MemberInfo>>({
    method: 'get',
    url: `organizations/${params.id}/users`,
    params: { ...params.query },
  })
}

export const inviteMembers = (data: InviteMembers) => {
  return client<unknown>({
    method: 'post',
    url: `organizations/${data.id}/users-batch`,
    data,
  })
}

export const validateMember = (orgId: number, params: { mail?: string | null; user_id?: string | null }) => {
  return client<MemberInfo>({
    method: 'GET',
    url: `organizations/${orgId}/user/invite/search`,
    params,
  })
}

export const deleteMember = (params: DeleteOrgMember) => {
  return client<unknown>({
    method: 'delete',
    url: `organizations/${params.id}/users/${params?.query?.id}`,
  })
}

export const updateMemberRoles = (data: UpdateUserRole) => {
  return client<unknown>({
    url: `organizations/${data.id}/users/${data.user_id}/roles`,
    method: 'put',
    data: {
      ...data.query,
    },
  })
}

export const updateMemberPerms = (data: { id: number; user_id: number; perm_ids: number[] }) => {
  return client<unknown>({
    url: `organizations/${data.id}/users/${data.user_id}/perms`,
    method: 'put',
    data,
  })
}

export const getRoles = (id: number) => {
  return client<ListResponse<RoleInfo>>({
    url: `organizations/${id}/roles/brief`,
    method: 'get',
  })
}

export const getDetailRols = (params: GetRoles) => {
  return client<ListResponse<RoleInfo>>({
    url: `organizations/${params.id}/roles`,
    method: 'get',
    params: { ...params.query },
  })
}

export const deleteRole = (params: DeleteRole) => {
  return client<unknown>({
    url: `organizations/${params.id}/roles/${params.role_ids}`,
    method: 'delete',
  })
}

export const getPermissions = (params: GetOrgMembers) => {
  return client<ListResponse<PermissionInfo>>({
    url: `organizations/${params.id}/permissions/brief`,
    method: 'get',
    params: { ...params.query },
  })
}

export const getDetailPermissions = (params: GetOrgMembers) => {
  return client<ListResponse<PermissionInfo>>({
    url: `organizations/${params.id}/permissions`,
    method: 'get',
    params: { ...params.query },
  })
}

export const updateRolePermissions = (data: UpdateRolePermission) => {
  return client<unknown>({
    url: `organizations/${data.id}/roles/${data.role_id}`,
    method: 'put',
    data: { perms: data.perms },
  })
}

export const createRoles = (data: CreateRoles) => {
  return client<unknown>({
    url: `organizations/${data.id}/roles-batch`,
    method: 'post',
    data,
  })
}

export const getAPIToken = (params: GetOrgMembers) => {
  return client<ListResponse<APITokenInfo>>({
    url: `api_tokens/organizations/${params.id}`,
    method: 'get',
    params: {
      ...params.query,
    },
  })
}

export const deleteAPIToken = (params: { org_id: number; id: number }) => {
  return client<unknown>({
    url: `api_tokens/${params.id}`,
    params,
    method: 'delete',
  })
}

export const createApiToken = (data: CrateAPITokenInfo & { org_id: number }) => {
  return client<{ token: string }>({
    url: 'api_tokens',
    method: 'post',
    data,
  })
}

export const putApiToken = (data: { id: number; org_id: number; perms: number[] }) => {
  return client<unknown>({
    url: `api_tokens/${data.id}`,
    method: 'put',
    data,
  })
}

export const putOrg = (data: PutOrgReq) => {
  return client<unknown>({
    url: `organizations/${data.id}`,
    method: 'put',
    data,
  })
}
